import React, { useEffect, useMemo, useState } from 'react';
import { PencilLine, Plus, Search, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import AddRoleModal from './AddRoleModal';
import AssignUsersModal from './AssignUsersModal';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import { apiService } from '../../../../components/api/apiService';
import { roleService, type RoleDefinition } from '../../../../components/api/roleService';
import { isExternalRole, type RoleWithUsersDto, type User } from '../../interfaces/Users';
import { useAccessTheme } from './useAccessTheme';
import ViewRoutePrivilegesModal, { type RoutePrivilege } from '../Access/ViewRoutePrivilegesModal';
import { mergeTemplateWithDb } from '../Access/mergeRoutePrivileges';
import { mockRoutes } from '../Access/routePrivilegeTemplates';
import { logAccess } from './accessSettingsLogger';
import { ACCESS_TENANT_ID } from './accessDirectoryUtils';

interface RolesPermissionsPanelProps {
  openAddRole: boolean;
  onOpenAddRoleChange: (open: boolean) => void;
}

const RolesPermissionsPanel: React.FC<RolesPermissionsPanelProps> = ({
  openAddRole,
  onOpenAddRoleChange,
}) => {
  const t = useAccessTheme();
  const [search, setSearch] = useState('');
  const [roleDefs, setRoleDefs] = useState<RoleDefinition[]>([]);
  const [rolesWithUsers, setRolesWithUsers] = useState<RoleWithUsersDto[]>([]);
  const [loadingDefs, setLoadingDefs] = useState(true);
  const [loadingAssigned, setLoadingAssigned] = useState(true);
  const [defToDelete, setDefToDelete] = useState<RoleDefinition | null>(null);
  const [assignedToDelete, setAssignedToDelete] = useState<RoleWithUsersDto | null>(null);
  const [deletingAssigned, setDeletingAssigned] = useState(false);
  const [editRole, setEditRole] = useState<RoleDefinition | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [allSystemUsers, setAllSystemUsers] = useState<User[]>([]);
  const [activeRole, setActiveRole] = useState<RoleWithUsersDto | null>(null);
  const [showRoutesModal, setShowRoutesModal] = useState(false);
  const [routesForRole, setRoutesForRole] = useState<RoutePrivilege[]>([]);
  const [firstPageIdForRole, setFirstPageIdForRole] = useState<string | null>(null);

  const refreshRoleDefinitions = async () => {
    setLoadingDefs(true);
    try {
      const res = await roleService.getRoleDefinitions();
      setRoleDefs(res || []);
      logAccess('app', 'role_definitions_loaded', { count: (res || []).length });
    } catch (err) {
      logAccess('error', 'role_definitions_load_failed', {
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoadingDefs(false);
    }
  };

  const refreshRolesWithUsers = async () => {
    setLoadingAssigned(true);
    try {
      const response = await apiService.get('/api/auth/roles/users', {}, 'backend');
      const list = Array.isArray(response) ? response : [];
      setRolesWithUsers(list);
      logAccess('app', 'roles_with_users_loaded', { count: list.length });
    } catch (err) {
      logAccess('error', 'roles_with_users_load_failed', {
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoadingAssigned(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([refreshRoleDefinitions(), refreshRolesWithUsers()]);
  };

  useEffect(() => {
    void refreshAll();
  }, []);

  useEffect(() => {
    if (!openAddRole) return;
    setEditRole(null);
    setShowRoleModal(true);
  }, [openAddRole]);

  const closeRoleModal = () => {
    setShowRoleModal(false);
    setEditRole(null);
    onOpenAddRoleChange(false);
  };

  const filteredDefs = useMemo(
    () =>
      roleDefs.filter(
        (role) =>
          role.roleName.toLowerCase().includes(search.toLowerCase()) ||
          role.description.toLowerCase().includes(search.toLowerCase()) ||
          role.id.toLowerCase().includes(search.toLowerCase()) ||
          (role.createdBy || '').toLowerCase().includes(search.toLowerCase())
      ),
    [roleDefs, search]
  );

  const handleDeleteDefinition = async () => {
    if (!defToDelete) return;
    try {
      await roleService.deleteRole(defToDelete.id);
      setRoleDefs((prev) => prev.filter((role) => role.id !== defToDelete.id));
      await refreshRolesWithUsers();
      logAccess('app', 'role_deleted', { roleId: defToDelete.id, source: 'definitions' });
    } catch (err) {
      logAccess('error', 'role_delete_failed', {
        roleId: defToDelete.id,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setDefToDelete(null);
    }
  };

  const handleDeleteAssignedRole = async () => {
    if (!assignedToDelete) return;
    setDeletingAssigned(true);
    try {
      await roleService.deleteRole(assignedToDelete.roleId);
      await refreshAll();
      logAccess('app', 'role_deleted', { roleId: assignedToDelete.roleId, source: 'assignments' });
    } catch (err) {
      logAccess('error', 'role_delete_failed', {
        roleId: assignedToDelete.roleId,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setDeletingAssigned(false);
      setAssignedToDelete(null);
    }
  };

  const handleAssignClick = async (role: RoleWithUsersDto) => {
    try {
      setActiveRole(role);
      const response = await apiService.get('/api/auth/users', {}, 'backend');
      setAllSystemUsers(Array.isArray(response) ? response : []);
      setShowAssignModal(true);
      logAccess('app', 'assign_users_opened', { roleId: role.roleId });
    } catch (error) {
      logAccess('error', 'assign_users_load_failed', {
        roleId: role.roleId,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleAssignUser = async (userId: string) => {
    try {
      await apiService.post(
        '/api/auth/users/updaterole',
        {
          userID: userId,
          id: userId,
          roleID: activeRole?.roleId,
          tenantId: ACCESS_TENANT_ID,
        },
        'backend'
      );
      await refreshAll();
      setShowAssignModal(false);
      logAccess('app', 'user_assigned_to_role', { userId, roleId: activeRole?.roleId });
    } catch (error) {
      logAccess('error', 'user_assign_failed', {
        userId,
        roleId: activeRole?.roleId,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await apiService.post(
        '/api/auth/roles/remove-user',
        {
          userID: userId,
          roleID: activeRole?.roleId,
          tenantId: ACCESS_TENANT_ID,
        },
        'backend'
      );
      await refreshAll();
      setShowAssignModal(false);
      logAccess('app', 'user_removed_from_role', { userId, roleId: activeRole?.roleId });
    } catch (error) {
      logAccess('error', 'user_remove_from_role_failed', {
        userId,
        roleId: activeRole?.roleId,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleViewRoutes = async (role: RoleWithUsersDto) => {
    const template = mockRoutes;
    try {
      const response = await apiService.get(
        '/api/RoutePermission',
        {
          tenantId: ACCESS_TENANT_ID,
          roleId: role.roleId,
        },
        'backend'
      );
      const payload = Array.isArray(response) ? { routes: response, firstPageId: null } : response || {};
      const dbRoutes = Array.isArray(payload.routes) ? payload.routes : [];
      setRoutesForRole(mergeTemplateWithDb(template, dbRoutes));
      setFirstPageIdForRole(payload.firstPageId || null);
      setActiveRole(role);
      setShowRoutesModal(true);
      logAccess('app', 'role_routes_opened', { roleId: role.roleId });
    } catch (error) {
      logAccess('error', 'role_routes_load_failed', {
        roleId: role.roleId,
        message: error instanceof Error ? error.message : String(error),
      });
      setRoutesForRole(mergeTemplateWithDb(template, []));
      setFirstPageIdForRole(null);
      setActiveRole(role);
      setShowRoutesModal(true);
    }
  };

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div>
          <h3 className={`text-[15px] font-semibold tracking-tight ${t.title}`}>Role definitions</h3>
          <p className={`text-sm mt-1 leading-relaxed ${t.muted}`}>Define and manage user roles.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${t.muted}`} />
            <input
              type="text"
              placeholder="Search by name, id, or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full border rounded-lg pl-9 pr-3 h-9 text-sm outline-none ${t.input}`}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setEditRole(null);
              onOpenAddRoleChange(true);
            }}
            className={`inline-flex items-center gap-2 px-4 h-9 text-sm font-medium rounded-lg shadow-sm ${t.btnPrimary}`}
          >
            <Plus className="w-4 h-4" /> Add role
          </button>
        </div>
        <div className={`overflow-hidden rounded-xl border ${t.card}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className={`${t.directoryHead} text-[11px] uppercase tracking-wider`}>
                <tr>
                  <th className="px-4 py-3 font-medium">Role Name</th>
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium">Admin</th>
                  <th className="px-4 py-3 font-medium">Active</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingDefs ? (
                  <tr>
                    <td colSpan={5} className={`px-4 py-12 text-center ${t.muted}`}>Loading roles...</td>
                  </tr>
                ) : filteredDefs.length > 0 ? (
                  filteredDefs.map((role) => (
                    <tr key={role.id} className={`border-t last:border-b-0 ${t.rowBorder} ${t.rowHover}`}>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`font-medium ${t.title}`}>{role.roleName}</div>
                          {isExternalRole(role) ? (
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide border ${t.badge}`}>
                              Synced
                            </span>
                          ) : null}
                        </div>
                        <div className={`text-[10px] font-mono mt-0.5 truncate max-w-[160px] ${t.muted}`} title={role.id}>
                          {role.id}
                        </div>
                      </td>
                      <td className={`px-4 py-3.5 max-w-[240px] ${t.body}`}>
                        <span className="line-clamp-2">{role.description || '—'}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${role.isAdmin ? t.badgeAccent : t.badge}`}>
                          {role.isAdmin ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${role.isActive ? t.badgeAccent : t.badge}`}>
                          {role.isActive ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              setEditRole(role);
                              setShowRoleModal(true);
                            }}
                            className={`p-1.5 rounded-lg ${t.muted} ${t.actionHover} hover:text-indigo-500`}
                            title="Edit"
                          >
                            <PencilLine className="w-4 h-4" />
                          </button>
                          {isExternalRole(role) && !role.isAdmin ? (
                            <button
                              type="button"
                              onClick={() => setDefToDelete(role)}
                              className={`p-1.5 rounded-lg ${t.muted} ${t.actionHover} hover:text-red-500`}
                              title="Delete synced role"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className={`text-center py-12 ${t.muted}`}>No roles found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h3 className={`text-[15px] font-semibold tracking-tight ${t.title}`}>Role access</h3>
          <p className={`text-sm mt-1 leading-relaxed ${t.muted}`}>Assign users and configure route privileges for each role.</p>
        </div>
        <div className={`overflow-hidden rounded-xl border ${t.card}`}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className={`${t.directoryHead} text-[11px] uppercase tracking-wider`}>
                <tr>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Admin</th>
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium">Assigned Users</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingAssigned ? (
                  <tr>
                    <td colSpan={5} className={`px-4 py-6 text-center ${t.muted}`}>Loading...</td>
                  </tr>
                ) : rolesWithUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={`px-4 py-6 text-center ${t.muted}`}>No roles found</td>
                  </tr>
                ) : (
                  rolesWithUsers.map((role) => (
                    <tr key={role.roleId} className={`border-t last:border-b-0 ${t.rowBorder} ${t.rowHover}`}>
                      <td className={`px-4 py-3.5 font-medium ${t.title}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{role.roleName}</span>
                          {isExternalRole(role) ? (
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide border ${t.badge}`}>
                              Synced
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className={`px-4 py-3.5 ${t.body}`}>{role.isadmin ? 'Yes' : 'No'}</td>
                      <td className={`px-4 py-3.5 ${t.body}`}>{role.description}</td>
                      <td className={`px-4 py-3.5 ${t.body}`}>
                        {role.assignedUsers.length > 0 ? (
                          role.assignedUsers.map((user) => user.email).join(', ')
                        ) : (
                          <span className={t.muted}>No users</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <div className="inline-flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 justify-end">
                          <button
                            type="button"
                            onClick={() => void handleAssignClick(role)}
                            className={`text-sm flex items-center gap-1 ${t.accentText} hover:underline`}
                          >
                            <UserPlus className="w-4 h-4" /> Assign Users
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleViewRoutes(role)}
                            className={`text-sm flex items-center gap-1 ${t.accentText} hover:underline`}
                          >
                            <ShieldCheck className="w-4 h-4" /> Access Setting
                          </button>
                          {isExternalRole(role) && !role.isadmin ? (
                            <button
                              type="button"
                              onClick={() => setAssignedToDelete(role)}
                              className="text-sm flex items-center gap-1 text-red-500 hover:underline"
                            >
                              <Trash2 className="w-4 h-4" /> Delete
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showRoleModal ? (
        <AddRoleModal
          key={editRole?.id || 'new'}
          role={editRole}
          onClose={closeRoleModal}
          onSubmit={() => {
            closeRoleModal();
            void refreshAll();
          }}
        />
      ) : null}

      {showAssignModal && activeRole ? (
        <AssignUsersModal
          roleName={activeRole.roleName}
          assignedUsers={activeRole.assignedUsers.map((user) => ({
            id: user.userId,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            isActive: true,
          }))}
          availableUsers={allSystemUsers}
          onClose={() => setShowAssignModal(false)}
          onAssign={handleAssignUser}
          onRemove={handleRemoveUser}
        />
      ) : null}

      {showRoutesModal && activeRole ? (
        <ViewRoutePrivilegesModal
          roleName={activeRole.roleName}
          roleId={activeRole.roleId}
          tenantId={ACCESS_TENANT_ID}
          routes={routesForRole}
          firstPageId={firstPageIdForRole}
          onClose={() => setShowRoutesModal(false)}
        />
      ) : null}

      {defToDelete ? (
        <ConfirmDialog
          title="Delete synced role?"
          message={
            'Deleting "' +
            defToDelete.roleName +
            '" will also permanently remove ' +
            defToDelete.totalUsers +
            ' synced user(s) assigned to this role. This cannot be undone.'
          }
          onYes={handleDeleteDefinition}
          onNo={() => setDefToDelete(null)}
          onCancel={() => setDefToDelete(null)}
        />
      ) : null}

      {assignedToDelete ? (
        <ConfirmDialog
          title="Delete synced role?"
          message={`Deleting "${assignedToDelete.roleName}" will also permanently remove ${assignedToDelete.assignedUsers.length} synced user(s) assigned to this role. This cannot be undone.`}
          onYes={deletingAssigned ? () => undefined : handleDeleteAssignedRole}
          onNo={() => {
            if (!deletingAssigned) setAssignedToDelete(null);
          }}
          onCancel={() => {
            if (!deletingAssigned) setAssignedToDelete(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default RolesPermissionsPanel;
